<template>
  <div class="d-flex sub-nav">
    <assessment-type-select class="mr-1 mr-md-4"></assessment-type-select>
    <property-tenure-select class="mr-1 mr-md-4"></property-tenure-select>
    <work-plan-select v-if="isDashboard && hasPermissionToManageWorkPlans" class="mr-1 mr-md-4" />
    <scrollable-widget
        arrow-bg-color="var(--secondary)"
        arrow-fg-color="var(--gray-dark)"
        class="nav-items d-none d-sm-flex"
        :class="[{'has-actions': hasActions, 'dashboard-inner-menu-container': isDashboard}]"
        :refresh="hasActions"
    >
      <b-navbar>
        <b-navbar-nav>
          <b-nav-item
              class="nav-item"
              :active="$route.name === 'actions' && item.label === 'Open'"
              v-for="(item, key) in statuses"
              :key="`action-nav-${key}`"
              :to="item.route"
              :id="item.label"
              v-if="!item.hidden && hasPermissionToFilterStatus(item)"
              exact-active-class="active"
          >
            {{ item.label }}
            <status-counter
                v-if="item.status"
                :hidden="item.status === 'actionFinder'"
                :status="item.status"
                :bg-color="item.statusColor"
                :count="counts[item.status.toLowerCase()]"
            />
          </b-nav-item>
        </b-navbar-nav>
      </b-navbar>
    </scrollable-widget>
    <b-navbar class="d-sm-none flex-grow-1 pr-0">
      <b-navbar-nav class="w-100 justify-content-between">
        <div class="d-flex justify-content-between">
          <b-nav-item-dropdown no-caret>
            <template #button-content>
              <font-awesome-icon
                  :icon="['far', 'bars']"
                  size="lg"
                  class="text-dark"
              >
              </font-awesome-icon>
            </template>
            <b-dropdown-item
                v-for="(item, key) in statuses"
                :key="`action-small-nav-${key}`"
                :to="item.route"
                v-if="!item.hidden && hasPermissionToFilterStatus(item)"
                exact
                exact-active-class="active"
            >
              {{ item.label }}
              <status-counter
                  v-if="item.status"
                  :open-count="openActionsCount"
                  :status="item.status"
                  :bg-color="item.statusColor"
                  :count="counts[item.status.toLowerCase()]"
              />
            </b-dropdown-item>
          </b-nav-item-dropdown>
          <b-nav-text class="ml-2 py-1 px-1 text-dark">{{ activeLink }}</b-nav-text>
        </div>
        <b-nav-item
            v-if="!$route.params.clientActionId && $route.name !== 'analysis'"
            href="#"
            @click.prevent="onSearchClick"
            :active="showSearch"
        >
          <font-awesome-icon :icon="['fal', 'search']"/>
        </b-nav-item>
      </b-navbar-nav>
    </b-navbar>
    <b-navbar v-if="hasActions" class="ml-auto py-1 px-3" ref="actionsMenu">
      <portal-target name="actions-subnav"/>
    </b-navbar>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import StatusCounter from '@/components/Action/StatusCounter';
import ActionStatus from '@/services/Action/ActionStatus';
import ScrollableWidget from '@/components/Widgets/ScrollableWidget';
import AssessmentTypeSelect from '@/components/Nav/AssessmentTypeSelect';
import PropertyTenureSelect from '@/components/Nav/PropertyTenureSelect';
import WorkPlanSelect from '@/components/Nav/WorkPlanSelect';

import ActionsMenuVisibilityMixin from '@/components/Nav/ActionsMenuVisibilityMixin';
import {
  ACTIONS_MANAGE_WORK_PLANS,
  ACTIONS_VIEW_ALL,
  PERMISSION_TYPE_ACTION,
  ACTIONS_VIEW_TEAM_ACTIONS,
  ACTIONS_ASSIGN,
  ACTIONS_APPROVE,
  ACTIONS_BULK_APPROVE_SUBMITTED,
  ACTIONS_REJECT,
} from '@/config/permission';
import {FEATURE_ACTION_FINDER, isFeatureEnabled} from '@/config/features';

export default {
  name: 'Action-Navbar',
  components: {
    StatusCounter,
    ScrollableWidget,
    AssessmentTypeSelect,
    PropertyTenureSelect,
    WorkPlanSelect,
  },
  mixins: [ActionsMenuVisibilityMixin],
  data() {
    return {
      showAnalysis: false,
      statuses: [
        {
          label: 'Action Finder',
          route: {
            name: 'actionFinder',
          },
          status: 'actionFinder',
          hidden: !isFeatureEnabled(FEATURE_ACTION_FINDER),
          isBasicAccess: true,
        },
        {
          label: 'Approved',
          route: {
            name: 'actionStatus',
            params: {
              statusFilter: 'approved',
            },
          },
          status: ActionStatus.APPROVED(),
          statusColor: '#2A2638',
          isBasicAccess: true,
        },
        {
          label: 'Open',
          route: {
            name: 'actions',
            params: {
              statusFilter: 'open',
            },
          },
          status: 'Open',
          statusColor: '#07792d',
          isBasicAccess: true,
        },
        {
          label: 'Unassigned',
          route: {
            name: 'actionStatus',
            params: {
              statusFilter: 'unassigned',
            },
          },
          status: ActionStatus.UNASSIGNED(),
          statusColor: '#638BC2',
          isBasicAccess: false,
        },
        {
          label: 'Assigned',
          route: {
            name: 'actionStatus',
            params: {
              statusFilter: 'assigned',
            },
          },
          status: ActionStatus.ASSIGNED(),
          statusColor: '#638BC2',
          isBasicAccess: true,
        },
        {
          label: 'My Pending Approval',
          route: {
            name: 'actionStatus',
            params: {
              statusFilter: 'approvals',
            },
          },
          status: ActionStatus.PENDING_APPROVAL(),
          statusColor: '#FFA600',
          isBasicAccess: false,
        },
        {
          label: 'Partially complete',
          route: {
            name: 'actionStatus',
            params: {
              statusFilter: ActionStatus.PARTIAL(),
            },
          },
          status: ActionStatus.PARTIAL(),
          statusColor: '#07792d',
          isBasicAccess: true,
        },
        {
          label: 'Rejected',
          route: {
            name: 'actionStatus',
            params: {
              statusFilter: 'rejected',
            },
          },
          status: ActionStatus.REJECTED(),
          statusColor: '#9d4983',
          isBasicAccess: true,
        },
        {
          label: 'Queried',
          route: {
            name: 'actionStatus',
            params: {
              statusFilter: 'query',
            },
          },
          status: ActionStatus.QUERY(),
          statusColor: '#00D2B4',
          isBasicAccess: true,
        },
        {
          label: 'Responded to query',
          route: {
            name: 'actionStatus',
            params: {
              statusFilter: 'query-response',
            },
          },
          status: ActionStatus.QUERY_RESPONSE(),
          statusColor: '#0031d2',
          isBasicAccess: true,
        },
        {
          label: 'No Access',
          route: {
            name: 'actionStatus',
            params: {
              statusFilter: 'no-access',
            },
          },
          status: ActionStatus.NO_ACCESS(),
          statusColor: '#C388BB',
          isBasicAccess: false,
        },
        {
          label: 'Responded to no access',
          route: {
            name: 'actionStatus',
            params: {
              statusFilter: 'no-access-response',
            },
          },
          status: ActionStatus.NO_ACCESS_RESPONSE(),
          statusColor: '#5049a6',
          isBasicAccess: true,
        },
        {
          label: 'Analysis',
          route: { name: 'analysis' },
          isBasicAccess: true,
        },
      ],
    };
  },
  watch: {
    typeId() {
      this.showAnalysis = this.typeId;
    },
  },
  computed: {
    ...mapGetters({
      showSearch: 'actionShowSearch',
      typeId: 'complianceTypeId',
      actionsMenuVisible: 'actionsMenuVisible',
    }),
    hasActions() {
      return (this.$route.name !== 'analysis' || this.$route.name !== 'actionFinder');
    },
    activeLink() {
      if (!this.statuses) {
        return null;
      }

      const routeName = this.$route.name;
      const routeParams = this.$route.params;

      const activeRoute = this.statuses.find((item) => {
        if (item.route.name !== routeName) return false;

        if (routeName !== 'actionStatus') {
          return true;
        }

        if (routeParams.statusFilter) {
          return item.route.params.statusFilter === routeParams.statusFilter;
        }

        return true;
      });

      return activeRoute ? activeRoute.label : null;
    },

    hasPermissionToManageWorkPlans() {
      return this.$can(ACTIONS_MANAGE_WORK_PLANS, PERMISSION_TYPE_ACTION);
    },
    isDashboard() {
      return this.$route.name === 'actions-dashboard';
    },
    counts() {
      const statusCounts = this.$store.getters.counts;
      statusCounts.open = this.openActionsCount;
      return statusCounts;
    },
    openActionsCount() {
      if (!this.statuses) {
        return 0;
      }

      const { counts } = this.$store.getters;

      const openStatuses = ActionStatus.openStatuses();

      return this.statuses.reduce((total, item) => {
        if (!openStatuses.includes(item.status)) {
          return total;
        }

        if (item.status === 'pending approval') {
          const isAdmin = this.$can(ACTIONS_VIEW_ALL, PERMISSION_TYPE_ACTION);
          const isTeamManager = this.$can(ACTIONS_VIEW_TEAM_ACTIONS, PERMISSION_TYPE_ACTION);

          if (isAdmin || isTeamManager) {
            return total + (counts['pending approval unfiltered'] || 0);
          }

          return total;
        }

        if (!this.hasPermissionToFilterStatus(item)) {
          return total;
        }

        return total + (counts[item.status] || 0);
      }, 0);
    },
  },
  methods: {
    ...mapActions({ onSearchClick: 'searchActionClicked' }),
    hasPermissionToFilterStatus(item) {
      if (item.isBasicAccess) {
        return true;
      }

      const canViewAll = this.$can(ACTIONS_VIEW_ALL, PERMISSION_TYPE_ACTION);
      const isTeamManager = this.$can(ACTIONS_VIEW_TEAM_ACTIONS, PERMISSION_TYPE_ACTION);

      const canApproveAndReject = this.$can(ACTIONS_ASSIGN, PERMISSION_TYPE_ACTION) &&
          this.$can(ACTIONS_APPROVE, PERMISSION_TYPE_ACTION) &&
          this.$can(ACTIONS_BULK_APPROVE_SUBMITTED, PERMISSION_TYPE_ACTION) &&
          this.$can(ACTIONS_REJECT, PERMISSION_TYPE_ACTION);

      const status = item.status ? item.status.toLowerCase() : null;

      if (status === 'pending approval') {
        return (canViewAll || isTeamManager) && canApproveAndReject;
      }

      if (status === 'no access' && isTeamManager) {
        return true;
      }

      return canViewAll;
    },
  },
  mounted() {
    this.$store.dispatch('refreshCounts', this.$store.getters.complianceTypeId);
  },
  created() {
    if (this.hasPermissionToManageWorkPlans) {
      const workPlanTab = {
        label: 'Work plans',
        route: { name: 'work-plans' },
        isBasicAccess: true,
      };
      this.statuses.push(workPlanTab);
    }
  },

};
</script>

<style scoped>
.dashboard-inner-menu-container {
  max-width: calc(100vw - 620px) !important;
}

.nav-tooltip {
  font-size: 14px;
}
</style>
